import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRegistrationDeadline } from "@/hooks/useRegistrationDeadline";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Calendar, 
  MapPin, 
  Star, 
  Users, 
  ArrowRight
} from "lucide-react";
import { differenceInDays, differenceInHours, format, isPast, parseISO } from "date-fns";
import type { Challenge } from "./ChallengeBoard";
import ChallengeJoinFlow from "./ChallengeJoinFlow";

interface Event {
  id: string;
  name: string;
  event_date: string;
  location: string | null;
  cause: string | null;
  description: string | null;
}

interface FeaturedChallenge extends Challenge {
  event_id: string | null;
  is_featured: boolean;
}

interface FeaturedChallengeSpotlightProps {
  onChallengeClick: (challengeId: string) => void;
}

const FeaturedChallengeSpotlight: React.FC<FeaturedChallengeSpotlightProps> = ({
  onChallengeClick,
}) => {
  const { data: deadlineData } = useRegistrationDeadline();
  const [challenge, setChallenge] = useState<FeaturedChallenge | null>(null);
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [teamCount, setTeamCount] = useState(0);
  const [joinFlowOpen, setJoinFlowOpen] = useState(false);

  useEffect(() => {
    fetchFeaturedChallenge();
  }, []);

  const fetchFeaturedChallenge = async () => {
    try {
      const { data: challengeData, error: challengeError } = await supabase
        .from("challenges")
        .select("*")
        .eq("is_featured", true)
        .maybeSingle();

      if (challengeError) throw challengeError;

      if (!challengeData) {
        setLoading(false);
        return;
      }

      setChallenge(challengeData as FeaturedChallenge);

      if (challengeData.event_id) {
        const { data: eventData, error: eventError } = await supabase
          .from("events")
          .select("id, name, event_date, location, cause, description")
          .eq("id", challengeData.event_id)
          .single();

        if (!eventError && eventData) {
          if (isPast(parseISO(eventData.event_date))) {
            await supabase
              .from("challenges")
              .update({ is_featured: false })
              .eq("id", challengeData.id);
            setChallenge(null);
            setLoading(false);
            return;
          }
          setEvent(eventData);
        }
      }

      const { count } = await supabase
        .from("challenge_team_members")
        .select("*", { count: "exact", head: true })
        .eq("challenge_id", challengeData.id);

      setTeamCount(count || 0);
    } catch (error) {
      console.error("Error fetching featured challenge:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCountdown = (eventDate: string) => {
    const target = parseISO(eventDate);
    const now = new Date();
    const days = differenceInDays(target, now);
    
    if (days > 0) {
      return `${days} day${days === 1 ? "" : "s"} away`;
    }
    
    const hours = differenceInHours(target, now);
    if (hours > 0) {
      return `${hours} hour${hours === 1 ? "" : "s"} away`;
    }
    
    return "Today!";
  };

  if (loading) {
    return (
      <Card className="mb-8 border-primary/20 bg-card/60">
        <CardContent className="p-6">
          <Skeleton className="h-5 w-32 mb-4" />
          <Skeleton className="h-7 w-2/3 mb-2" />
          <Skeleton className="h-5 w-1/2" />
        </CardContent>
      </Card>
    );
  }

  if (!challenge) {
    return null;
  }

  const countdown = event ? getCountdown(event.event_date) : null;

  return (
    <Card className="mb-8 border-primary/30 bg-card/60 overflow-hidden">
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row md:gap-6">
          {/* Event Image */}
          <div className="w-full md:w-2/5 lg:w-1/3 mb-4 md:mb-0 md:order-2">
            <img 
              src="/assets/heritage-hall.svg" 
              alt="Heritage Hall at Phase Events"
              className="w-full h-48 md:h-full object-cover rounded-lg"
            />
          </div>

          {/* Content */}
          <div className="flex-1 md:order-1">
            {/* Header row: badge + countdown */}
            <div className="flex items-center justify-between gap-4 mb-4">
              <Badge 
                variant="outline" 
                className="bg-primary/10 text-primary border-primary/30 gap-1.5"
              >
                <Star className="h-3 w-3 fill-primary" />
                Featured Event
              </Badge>
              {countdown && (
                <span className="text-sm font-medium text-primary">
                  {countdown}
                </span>
              )}
            </div>

            {/* Title */}
            <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">
              {challenge.title}
            </h2>

            {/* One-liner */}
            {challenge.one_liner && (
              <p className="text-muted-foreground mb-4">
                {challenge.one_liner}
              </p>
            )}

            {/* Event details - compact horizontal layout */}
            {event && (
              <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-5">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  <span>{format(parseISO(event.event_date), "MMM d, yyyy")}</span>
                </div>
                {event.location && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" />
                    <span>{event.location}</span>
                  </div>
                )}
                {teamCount > 0 && (
                  <div className="flex items-center gap-1.5">
                    <Users className="h-4 w-4" />
                    <span>{teamCount} builder{teamCount !== 1 ? "s" : ""} joined</span>
                  </div>
                )}
              </div>
            )}

            {/* Single CTA */}
            <Button 
              size="lg"
              className="gap-2"
              onClick={() => setJoinFlowOpen(true)}
            >
              Join This Challenge
              <ArrowRight className="h-4 w-4" />
            </Button>
            {deadlineData && !deadlineData.isExpired && (
              <p className="text-xs text-muted-foreground mt-2">
                Free to participate • Registration closes {deadlineData.formattedDeadline}
              </p>
            )}
          </div>
        </div>
      </CardContent>

      {/* Join Flow Dialog */}
      {challenge && (
        <ChallengeJoinFlow
          open={joinFlowOpen}
          onOpenChange={setJoinFlowOpen}
          challengeId={challenge.id}
          eventId={challenge.event_id}
          challengeTitle={challenge.title}
          initialAction="join"
        />
      )}
    </Card>
  );
};

export default FeaturedChallengeSpotlight;